import { useState, type ReactNode } from 'react';
import { DropdownMenu } from './ui/DropDown/DropdownMenu';
import { DropdownMenuTrigger } from './ui/DropDown/DropdownMenuTrigger';
import { DropdownMenuContent } from './ui/DropDown/DropdownMenuContent';
import ToggleMenuBox from './ui/ToggleMenuBox';
import NavigationApps from './ui/NavigationApps';
import { Button } from './ui/Button';
import { useSubMenu } from './SubMenuContext';
import { Menu } from 'lucide-react';
import { useChat } from './context/ChatContext';
import Boss from './ui/appsIcons/Boss';
import { twMerge } from 'tailwind-merge';
import { useUser } from './UserContext';
import { AppNotificationsDrawer } from './notifications/AppNotificationsDrawer';
import { ActionBarSlot } from './ui/ActionBar';
import { useWindowDimensions } from './lib/hook/copyClipBoard';

interface TopBarProps {
    toggleSidebar?: (toggle?: boolean) => void;
    app: string;
    children?: ReactNode;
}

const LARGURA_CHAT = 364;
const GAP_MENUS = 8;
const SIDEBAR_FECHADA = 50;
const RESERVA_LATERAL = 140;
const BARRA_MINIMA = 260;

const TopBar = ({ app, children }: TopBarProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const { hideApps } = useUser();

    const { setSideBarOpen, isSidebarOpen, isContentOpen, sideWidth = 280 } = useSubMenu();

    const { toggleChat, isChatOpen } = useChat();
    const { width } = useWindowDimensions();

    const celular = width <= 460;
    const larguraSidebar = celular ? 0 : (isSidebarOpen ? sideWidth : SIDEBAR_FECHADA) + GAP_MENUS;
    const larguraTerceiro = celular || !isContentOpen ? 0 : sideWidth + GAP_MENUS;
    const larguraChat = isChatOpen && width >= 1024 ? LARGURA_CHAT : 0;

    const handleOnOpen = (state: boolean) => {
        setIsOpen(state);
    };

    return (
        <div className='relative w-full h-[45px] empreender-lib-topbar flex flex-row items-center bg-white mb-2 rounded-bl-xl '>
            <div className='min-w-0 h-full flex items-center !px-4 gap-x-1 cursor-pointer !pl-[5px] shrink-0'>
                <Button variant={'ghost'} size={'icon'} onClick={() => setSideBarOpen(!isSidebarOpen)} className='[&>svg]:size-4'>
                    <Menu size={16} />
                </Button>
                <div>
                    {!hideApps ? (
                        <DropdownMenu isOpen={isOpen} setIsOpen={handleOnOpen}>
                            <DropdownMenuTrigger>
                                <ToggleMenuBox app={app} isOpen={isOpen} />
                            </DropdownMenuTrigger>

                            <DropdownMenuContent className='!p-0 shadow-[0px_4px_20px_-6px_rgba(0,_0,_0,_0.12)] border-none rounded-lg'>
                                <NavigationApps />
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <ToggleMenuBox app={app} isOpen={isOpen} />
                    )}
                </div>
            </div>

            <div className='absolute inset-y-0 left-0 flex items-center !px-2 pointer-events-none transition-[right] duration-200' style={{ right: `${larguraChat}px` }}>
                <div className='shrink-0 transition-[width] duration-200' style={{ width: `${larguraSidebar}px` }} />
                <div className='shrink-0 transition-[width] duration-200' style={{ width: `${larguraTerceiro}px` }} />

                <div className='grow' style={{ flexBasis: `${RESERVA_LATERAL}px` }} />

                <div className='w-auto max-w-[600px] pointer-events-auto' style={{ minWidth: `${BARRA_MINIMA}px` }}>{celular ? null : <ActionBarSlot />}</div>

                <div className='grow flex items-center justify-end gap-1 pointer-events-auto' style={{ flexBasis: `${RESERVA_LATERAL}px` }}>
                    {children}
                    <AppNotificationsDrawer />
                    <Button variant={'secondary'} className={twMerge(`${isChatOpen ? 'text-white bg-gray-900' : 'text-gray-900 bg-white'}`)} onClick={() => toggleChat(!isChatOpen)}>
                        <Boss />
                        <span className='text-xs font-medium'>Ajuda</span>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TopBar;
